import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { JSX, ReactNode, RefObject } from 'react';
import { createContext, use, useEffect, useMemo, useRef, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { COVERAGE_SETTINGS_DEFAULT, type CoverageSettings } from 'ts/base/routing/search/schema/CoverageSettings';
import {
	TGA_COVERAGE_SOURCES_DEFAULT,
	type TgaCoverageSources
} from 'ts/base/routing/search/schema/TgaCoverageSources';
import { ArrayUtils } from 'ts/commons/ArrayUtils';
import { StringUtils } from 'ts/commons/StringUtils';
import type { FindingLikeType } from 'ts/perspectives/findings/FindingLikeType';
import type { ExtendedStatementPathElement } from 'ts/perspectives/metrics/code/ExtendedStatementPathElement';
import type { CodeResultInfo } from 'typedefs/CodeResultInfo';
import type { DecisionProbe } from 'typedefs/DecisionProbe';
import type { DerivedTestCoverageInfo } from 'typedefs/DerivedTestCoverageInfo';
import type { EExtendedResourceTypeEntry } from 'typedefs/EExtendedResourceType';
import { ELanguage } from 'typedefs/ELanguage';
import type { ElementLocationSubtype } from 'typedefs/ElementLocationSubtype';
import type { ELineCoverageEntry } from 'typedefs/ELineCoverage';
import { ELineCoverage } from 'typedefs/ELineCoverage';
import type { FindingContent } from 'typedefs/FindingContent';
import type { FormattedTokenElementInfo } from 'typedefs/FormattedTokenElementInfo';
import type { IssueReference } from 'typedefs/IssueReference';
import type { LineBasedChangeRegion } from 'typedefs/LineBasedChangeRegion';
import type { LineBasedMethodInfo } from 'typedefs/LineBasedMethodInfo';
import type { LineCoverageInfo } from 'typedefs/LineCoverageInfo';
import type { PreprocessorExpansionsTransport } from 'typedefs/PreprocessorExpansionsTransport';
import type { ProbeCoverageInfo } from 'typedefs/ProbeCoverageInfo';
import type { RefactoringSuggestion } from 'typedefs/RefactoringSuggestion';
import type { SimpleStatementCoverageProbe } from 'typedefs/SimpleStatementCoverageProbe';
import type { TestPathExecutionWrapper } from 'typedefs/TestPathExecutionWrapper';
import type { TextRegionLocation } from 'typedefs/TextRegionLocation';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import { createStore, type StoreApi, useStore } from 'zustand';
import { CodeLineSelection } from './CodeLineSelection';
import { CodeOffsetSelection } from './CodeOffsetSelection';
import { createFocusedCodeTokenStore, type FocusedCodeTokenState } from './CodeToken.focus';
import { createHighlightedCodeTokenStore, type HighlightedCodeTokenState } from './CodeToken.highlighting';
import type { ExpandedMacroElementInfo } from './ExpandedMacroElementInfo';
import type { FindingWithLayoutInfo } from './FindingsLayout';
import { type InCodeSearchResult, search } from './InCodeSearchUtils';
import { type RenderedCodeProps } from './RenderedCode';
import { buildDataflowInfos, type DataflowInfos } from './RenderedCode.dataflow';
import { type CodeDiff, getLineChangeTypes } from './RenderedCode.diff';
import { calculateLinesWithFindingIds, type MaybePendingFindingLikeType } from './RenderedCode.findings';
import { type LineSegment, useLineSegments } from './RenderedCode.segments';

const RenderedCodeContext = createContext<ExposedCodeOptions | undefined>(undefined);

/** Representations of code that can be rendered. */
export type RenderableCode =
	| FindingContent
	| FormattedTokenElementInfo
	| ExpandedMacroElementInfo
	| (CodeResultInfo & { uniformPath: string });

/** A simple wrapper for probe coverage data */
type ProbeCoverageContainer = {
	probesByLine: Map<number, DecisionProbe | SimpleStatementCoverageProbe>;
	/** The width of the column that is used for the coverage, in 'em' units. */
	columnWidthInEm: number;
};

/** The first and last line to show from the file. */
export type VisibleCodeRegion = {
	// The first user-visible line number (1-based, inclusive)
	startLine: number;
	// The last user-visible line number (1-based, inclusive)
	endLine: number;
};

/** A review finding that was just created by the user but potentially not stored on the server. */
export type PendingFinding = TrackedFinding & { location: TextRegionLocation };

type HighlightedLinesState = {
	selectedLines?: CodeLineSelection;
	flashHighlightedLine?: number;
};

type ExposedCodeOptions = PassThroughRenderedCodeProps & {
	/**
	 * Stores the first offsets for each macro group (when preprocessorExpansions are available). By "first", we mean
	 * that the previous token belongs either to another macro or to no macro at all.
	 *
	 * @see #getStartOffsetsOfPreprocessorExpansionGroups
	 */
	expansionGroupStartOffsets: Set<number>;
	anyTestExecutionsExist: boolean;
	uniformPath: string;
	linesWithTokens: Map<number, LineSegment[]>;
	issueReferencesByFileOffset: Map<number, IssueReference[]>;
	visibleRegion: VisibleCodeRegion;
	linesWithFindings: Map<number, FindingWithLayoutInfo[]>;
	maxColumnsOnAnyLine: number;
	/** All code lines (also includes lines that are not shown to the user) */
	lines: string[];
	/** Total number of lines of the file (also includes lines that are not shown to the user) */
	totalLineCount: number;
	/** The number of lines that are visible to the user. */
	visibleLineCount: number;
	wrapNonTokens: boolean;
	testPathsByLineNumber: Map<number, TestPathExecutionWrapper>;
	probeCoverageContainer: ProbeCoverageContainer;
	lineCoverageByLine: Map<number, ELineCoverageEntry>;
	methodInfosByStartLine: Map<number, LineBasedMethodInfo>;
	diffLineChangeTypes: Map<number, string>;
	codeChangeRegionsByLine: Map<number, LineBasedChangeRegion>;
	onAddPendingReviewFinding?: (finding: PendingFinding) => void;
	/** The language, which can be unknown for types like {@link CodeResultInfo} */
	language?: ELanguage;
	coverageSettings: CoverageSettings;
	tgaCoverageSources: TgaCoverageSources;
	highlightedLinesStore: StoreApi<HighlightedLinesState>;
	highlightedCodeTokenStore: StoreApi<HighlightedCodeTokenState>;
	focusedCodeTokenStore: StoreApi<FocusedCodeTokenState>;
	highlightMacros: boolean;
	searchResults: InCodeSearchResult[];
	fontSize: number;
	dataflowInfosByLine: Map<number, DataflowInfos>;
	imperativeHandleRef: RefObject<CodeContainerImperativeHandle | null>;
};

/** Returns the {@link ExposedCodeOptions}, or throws an error when not called inside a {@link RenderedCodeContext} */
export function useRenderedCodeOptions(): ExposedCodeOptions {
	const context = use(RenderedCodeContext);
	if (context === undefined) {
		throw new Error('useRenderedCodeOptions must be used within a RenderedCodeContext');
	}
	return context;
}

/** The options that control how a line should be scrolled to. */
export type ScrollToLineOptions = {
	/** The 1-based line number to scroll to. */
	line: number;
	/** Whether the given line should be aligned to the top of the code container or at its center (default is 'center'). */
	align?: 'start' | 'center';
	/** Whether the line should be flashed in color for a short moment to highlight it. */
	flash?: boolean;
	/** Whether the code container should also be scrolled into the viewport to ensure the line is visible afterward. */
	scrollIntoViewport?: boolean;
};

/**
 * Exposes functions to imperatively interact with the code scroll container, i.e., to enable scroll synchronization or
 * interactive scrolling based on other user events.
 */
export type CodeContainerImperativeHandle = {
	getScrollOffsetX: () => number;
	getScrollOffsetY: () => number;
	getCenterLine: () => number;
	scrollToOffsetX: (offsetX: number) => void;
	scrollToOffsetY: (offsetY: number) => void;
	/**
	 * Scrolls the code container to the given line optionally also scrolling the container itself into the view.
	 *
	 * @returns If a scroll operation was scheduled it will return the exact Y-offset in px that it will scroll to.
	 */
	scrollTo: (options: ScrollToLineOptions) => number;
	getVisibleRegion: () => VisibleCodeRegion;
};

/** The props that are passed to the RenderedCodeProvider which are passed down via the context with the same type. */
type PassThroughRenderedCodeProps = {
	// The main code element to render
	codeElement: RenderableCode;
	project: string;
	// The commit (branch + timestamp) for which the code is shown. Relevant, e.g., for links to other views.
	commit: UnresolvedCommitDescriptor;
	// Whether to hide the borders around the code. Defaults to 'false'
	hideBorder?: boolean;
	// Whether the file left outline should be shown where occurrences of an identifier are highlighted
	hideOutline?: boolean;
	// Whether to hide the context actions.
	hideContextMenu?: boolean;
	highlightedRefactoringSuggestion?: RefactoringSuggestion;
	onScroll?: () => void;
	/**
	 * If this function is given, the code snippet will show a hint to the user that Ctrl/Cmd clicking on code links
	 * will navigate to their definition. The function needs to ensure that the UI navigates to the corresponding
	 * location.
	 */
	onNavigateToDefinition?: (
		project: string,
		uniformPathOfOpenedFile: string,
		targetLocation: ElementLocationSubtype,
		imperativeHandle: CodeContainerImperativeHandle | null
	) => void;
	/** Preprocessor expansions (C/C++/Obj-C) macros) for the given file. */
	preprocessorExpansions?: PreprocessorExpansionsTransport;
	/**
	 * Whether review findings are enabled. If true, users can manually create review finings for arbitrary code
	 * selections.
	 */
	reviewFindingsEnabled?: boolean;
	highlightedSearchResult?: number;
	/**
	 * Methods known for the given snippet. This will render small icons next to methods in the code, which allows
	 * jumping to the method history view.
	 */
	methodInfos?: LineBasedMethodInfo[];
	initialScrollToLine?: ScrollToLineOptions;
};

/** The options for rendering code snippets. */
export type RenderedCodeProviderProps = PassThroughRenderedCodeProps & {
	children?: ReactNode;
	// The user-visible code region (default: all lines)
	visibleRegion?: VisibleCodeRegion;
	// The lines that should initially be selected
	defaultCodeSelection?: CodeLineSelection | CodeOffsetSelection;
	findings: FindingLikeType[];
	coverageSettings?: CoverageSettings;
	// The selected partitions for the coverage
	tgaCoverageSources?: TgaCoverageSources;
	// Known test paths for the given snippet
	testPaths?: TestPathExecutionWrapper[];
	// Change regions in the code can be shown when there is an active baseline setting from a previous view.
	codeChangeRegions?: LineBasedChangeRegion[];
	overwriteSearchResults?: InCodeSearchResult[];
	searchTerm?: string;
	setHighlightedSearchResult?: Callback<number>;
	setNumberOfSearchHits?: Callback<number>;
	highlightMacros?: boolean;
	// Font size used for rendering
	fontSize?: number;
	diff?: CodeDiff;
	// Line coverage for the current code snippet.
	lineCoverage?: LineCoverageInfo | DerivedTestCoverageInfo;
	// Probe coverage (a more fine-granular type of coverage. e.g. MC/DC coverage) for the given snippet.
	probeCoverage?: ProbeCoverageInfo;
	dataflowStatementPaths?: ExtendedStatementPathElement[];
	imperativeHandleRef?: RefObject<CodeContainerImperativeHandle | null>;
	pendingReviewFindings?: PendingFinding[];
	onAddPendingReviewFinding?: (finding: PendingFinding) => void;
};

function calculateChangeRegionsByLine(
	codeChangeRegions: LineBasedChangeRegion[]
): Map<number, LineBasedChangeRegion & { lastChangeTimestamp: number }> {
	const changeRegionsByLine = new Map<number, LineBasedChangeRegion & { lastChangeTimestamp: number }>();
	codeChangeRegions.forEach(region => {
		for (let lineNumber = region.start; lineNumber <= region.end; lineNumber++) {
			changeRegionsByLine.set(lineNumber, region);
		}
	});
	return changeRegionsByLine;
}

function getCodeReferencesByFileOffset(tokenElement: RenderableCode): Map<number, IssueReference[]> {
	if (!('issueReferences' in tokenElement)) {
		return new Map();
	}
	const referencedIssueIdsByFileOffset = new Map<number, IssueReference[]>();
	tokenElement.issueReferences.forEach(issueReference => {
		for (let i = 0; i < issueReference.commentOffsetsWithRegions.size; i++) {
			const commentBlockOffset = issueReference.commentOffsetsWithRegions.firstElements[i]!;

			const referencesForEntireBLock = referencedIssueIdsByFileOffset.get(commentBlockOffset) ?? [];
			referencesForEntireBLock.push(issueReference);
			referencedIssueIdsByFileOffset.set(commentBlockOffset, referencesForEntireBLock);
			const referenceRegion = issueReference.commentOffsetsWithRegions.secondElements[i]!;
			const offsetInFile = commentBlockOffset + referenceRegion.start;
			referencedIssueIdsByFileOffset.set(offsetInFile, referencesForEntireBLock);
		}
	});
	return referencedIssueIdsByFileOffset;
}

/**
 * Returns the char offsets of the first tokens in each preprocessor expansion group. The first token could be a
 * preprocessor directive such as "#if" or the name of a macro.
 */
function getStartOffsetsOfPreprocessorExpansionGroups(
	preprocessorExpansions: PreprocessorExpansionsTransport | undefined,
	tokenStartOffsets: number[]
) {
	const expansionGroupStartOffsets = new Set<number>();
	if (!preprocessorExpansions) {
		return expansionGroupStartOffsets;
	}
	let previousExpansionGroupId: number | undefined = undefined;
	for (const offset of tokenStartOffsets.reverse()) {
		const currentExpansionGroupId = preprocessorExpansions.offsetToExpansionGroup[offset];
		if (currentExpansionGroupId != null && currentExpansionGroupId !== previousExpansionGroupId) {
			expansionGroupStartOffsets.add(offset);
		}
		previousExpansionGroupId = currentExpansionGroupId;
	}
	return expansionGroupStartOffsets;
}

/**
 * Returns the (inclusive) start offsets of the tokens in the given code element. The start offsets are char offsets
 * counted from the file start.
 *
 * To get the offsets, we take every third number from the "tokens" list. The format is defined in CodeResultInfo.tokens
 */
function determineTokenStartOffsets(codeElement: RenderableCode) {
	return codeElement.tokens.filter((value, index) => index % 3 === 0);
}

function isMacroExpandedOrCollapsed(
	currentExpandedExpansionsGroupIds: number[],
	previousExpandedExpansionGroupIds: number[] | undefined,
	storedExpandedExpansionGroupIds: Set<number>
) {
	if (currentExpandedExpansionsGroupIds.length > 0) {
		// Indicates that a macro was expanded
		return true;
	}

	if (previousExpandedExpansionGroupIds === undefined) {
		return false;
	}

	if (previousExpandedExpansionGroupIds.length > 0) {
		for (const id of previousExpandedExpansionGroupIds) {
			if (!storedExpandedExpansionGroupIds.has(id)) {
				// Indicates that a macro was collapsed
				return true;
			}
		}
	}

	return false;
}

function expandedMacroHasSearchResult(results: InCodeSearchResult[]) {
	return results.length === 1 && results[0]!.macroMatches.size === 1;
}

function doSearch(
	project: string,
	searchTerm: string | undefined,
	uniformPath: string,
	commit: UnresolvedCommitDescriptor,
	linesWithTokens: Map<number, LineSegment[]>,
	expandedExpansionGroupIds: number[],
	expandedExpansionGroupIdsRef: { current: number[] | undefined },
	highlightedCodeTokenStore: StoreApi<HighlightedCodeTokenState>,
	setSearchResults: Callback<InCodeSearchResult[]>,
	setNumberOfSearchHits: Callback<number>,
	setHighlightedSearchResult: Callback<number>
) {
	if (StringUtils.isEmptyOrWhitespace(searchTerm)) {
		setSearchResults([]);
		setNumberOfSearchHits(0);
		setHighlightedSearchResult(-1);
		return;
	}

	void search(project, uniformPath, commit, searchTerm, linesWithTokens, expandedExpansionGroupIds).then(
		(results: InCodeSearchResult[]) => {
			setSearchResults(results);
			setNumberOfSearchHits(results.length);
			const macroIsExpandedOrCollapsed = isMacroExpandedOrCollapsed(
				expandedExpansionGroupIds,
				expandedExpansionGroupIdsRef.current,
				highlightedCodeTokenStore.getState().expandedExpansionGroupIds
			);
			if ((macroIsExpandedOrCollapsed && expandedMacroHasSearchResult(results)) || !macroIsExpandedOrCollapsed) {
				setHighlightedSearchResult(results.length > 0 ? 0 : -1);
			}
		}
	);

	if (expandedExpansionGroupIds.length > 0) {
		expandedExpansionGroupIdsRef.current = expandedExpansionGroupIds;
	}
}

/**
 * Context provider for rendering a code snippet. This should not be used directly but only via RenderedCode or
 * RenderedExpandedMacroCode.
 */
export function RenderedCodeContextProvider({
	children,
	project,
	codeElement,
	visibleRegion,
	commit,
	methodInfos,
	preprocessorExpansions,
	findings,
	testPaths,
	codeChangeRegions,
	coverageSettings,
	tgaCoverageSources,
	hideContextMenu,
	initialScrollToLine,
	searchTerm,
	setNumberOfSearchHits,
	setHighlightedSearchResult,
	overwriteSearchResults,
	highlightMacros,
	fontSize = 15,
	diff,
	defaultCodeSelection,
	imperativeHandleRef,
	lineCoverage,
	probeCoverage,
	dataflowStatementPaths,
	pendingReviewFindings = [],
	onAddPendingReviewFinding,
	...passThroughProps
}: RenderedCodeProps): JSX.Element {
	const lines = StringUtils.splitLines(codeElement.text);
	if ('firstLine' in codeElement) {
		lines.unshift(...ArrayUtils.repeat('', codeElement.firstLine - 1));
	}
	const totalLineCount = lines.length;
	const normalizedVisibleRegion = normalizeVisibleRegion(visibleRegion, totalLineCount);

	let extendedResourceTypes: EExtendedResourceTypeEntry[] = [];
	if ('extendedResourceTypes' in codeElement) {
		extendedResourceTypes = codeElement.extendedResourceTypes;
	}
	const lineStartOffsets = getLineStartOffsets(lines, codeElement);

	const allFindings: MaybePendingFindingLikeType[] = [
		...findings.map(finding => ({ ...finding, isPending: false })),
		...pendingReviewFindings.map(pendingFinding => ({ ...pendingFinding, isPending: true }))
	];

	const { linesWithFindings, maxColumnsOnAnyLine } = calculateLinesWithFindingIds(
		allFindings,
		codeElement,
		lineStartOffsets,
		extendedResourceTypes,
		totalLineCount
	);

	const findingsById: Record<string, MaybePendingFindingLikeType> = {};
	allFindings.forEach(finding => (findingsById[finding.id] = finding));

	const methodInfosByStartLine = new Map<number, LineBasedMethodInfo>();
	methodInfos?.forEach(methodInfo => methodInfosByStartLine.set(methodInfo.lineBasedRegion.start, methodInfo));

	const diffLineChangeTypes = getLineChangeTypes(diff);

	const lineCoverageByLine = buildLineCoverageByLine(lineCoverage);
	const probeCoverageContainer = buildProbeCoverageContainer(probeCoverage);

	const testPathsByLine = new Map<number, TestPathExecutionWrapper>();
	testPaths?.forEach(testPathWrapper => testPathsByLine.set(testPathWrapper.startLine + 1, testPathWrapper));

	const anyTestExecutionsExist = (testPaths?.length ?? 0) > 0;
	const linesWithTokens = useLineSegments(
		lines,
		codeElement,
		lineStartOffsets,
		project,
		preprocessorExpansions,
		diff
	);

	const dataflowInfosByLine = useMemo(
		() => (dataflowStatementPaths ? buildDataflowInfos(dataflowStatementPaths) : new Map<number, DataflowInfos>()),
		[dataflowStatementPaths]
	);

	const referencedIssueIdsByFileOffset = getCodeReferencesByFileOffset(codeElement);

	const codeLineSelection = useMemo(
		() => asCodeLineSelection(defaultCodeSelection, lineStartOffsets),
		[defaultCodeSelection, lineStartOffsets]
	);
	// Set initial state based on the defaultCodeSelection
	const [highlightedLinesStore] = useState(() =>
		createStore<HighlightedLinesState>(() => ({
			selectedLines: codeLineSelection
		}))
	);
	const [highlightedCodeTokenStore] = useState(createHighlightedCodeTokenStore);
	const [focusedCodeTokenStore] = useState(createFocusedCodeTokenStore);

	const codeChangeRegionsByLine = calculateChangeRegionsByLine(codeChangeRegions ?? []);

	const firstMacroTokensOffsets = getStartOffsetsOfPreprocessorExpansionGroups(
		preprocessorExpansions,
		determineTokenStartOffsets(codeElement)
	);

	const [searchResults, setSearchResults] = useState<InCodeSearchResult[]>([]);
	const expandedExpansionGroupIds: number[] | undefined = Array.from(
		useStore(highlightedCodeTokenStore, state => state.expandedExpansionGroupIds)
	);
	const expandedExpansionGroupIdsRef = useRef<number[]>([]);

	useEffect(
		() => {
			if (overwriteSearchResults !== undefined) {
				setSearchResults(overwriteSearchResults);
				return () => {
					/* NO OP */
				};
			}
			if (!setNumberOfSearchHits || !setHighlightedSearchResult) {
				// Search is not supported by the view
				return () => {
					/* NO OP */
				};
			}

			// Searching is expensive. We don't need to search for every key the user presses
			const delayedSearch = setTimeout(
				() =>
					doSearch(
						project,
						searchTerm,
						codeElement.uniformPath,
						commit,
						linesWithTokens,
						expandedExpansionGroupIds,
						expandedExpansionGroupIdsRef,
						highlightedCodeTokenStore,
						setSearchResults,
						setNumberOfSearchHits,
						setHighlightedSearchResult
					),
				100
			);

			return () => clearTimeout(delayedSearch);
		}, // eslint-disable-next-line react-hooks/exhaustive-deps
		[
			project,
			codeElement.uniformPath,
			commit,
			searchTerm,
			setSearchResults,
			setNumberOfSearchHits,
			setHighlightedSearchResult,
			// This will detect a change in the expansion groups
			expandedExpansionGroupIds.length,
			// This will detect a change in overWriteSearchResults
			JSON.stringify(overwriteSearchResults) // eslint-disable-line react-hooks/exhaustive-deps
			// linesWithTokens will not change without a change of the other deps
		]
	);
	const internalImperativeHandleRef = useRef<CodeContainerImperativeHandle | null>(null);

	const scrollToLine = useMemo(
		() => initialScrollToLine ?? (codeLineSelection?.firstLine ? { line: codeLineSelection.firstLine } : undefined),
		[codeLineSelection?.firstLine, initialScrollToLine]
	);

	const value: ExposedCodeOptions = {
		...passThroughProps,
		codeElement,
		visibleRegion: normalizedVisibleRegion,
		visibleLineCount: normalizedVisibleRegion.endLine - normalizedVisibleRegion.startLine + 1,
		totalLineCount,
		anyTestExecutionsExist,
		linesWithTokens,
		issueReferencesByFileOffset: referencedIssueIdsByFileOffset,
		commit,
		project,
		preprocessorExpansions,
		uniformPath: codeElement.uniformPath,
		linesWithFindings,
		maxColumnsOnAnyLine,
		lines,
		probeCoverageContainer,
		lineCoverageByLine,
		wrapNonTokens: false,
		methodInfosByStartLine,
		testPathsByLineNumber: testPathsByLine,
		codeChangeRegionsByLine,
		diffLineChangeTypes,
		onAddPendingReviewFinding,
		language:
			'language' in codeElement
				? ELanguage.values.find(language => language.name === codeElement.language)
				: undefined,
		coverageSettings: coverageSettings ?? COVERAGE_SETTINGS_DEFAULT,
		tgaCoverageSources: tgaCoverageSources ?? TGA_COVERAGE_SOURCES_DEFAULT,
		hideContextMenu: Boolean(hideContextMenu),
		initialScrollToLine: scrollToLine,
		expansionGroupStartOffsets: firstMacroTokensOffsets,
		highlightedLinesStore,
		highlightedCodeTokenStore,
		focusedCodeTokenStore,
		highlightMacros: Boolean(highlightMacros),
		searchResults,
		fontSize,
		imperativeHandleRef: imperativeHandleRef ?? internalImperativeHandleRef,
		dataflowInfosByLine,
		methodInfos
	};

	return <RenderedCodeContext value={value}>{children}</RenderedCodeContext>;
}

function normalizeVisibleRegion(visibleRegion: VisibleCodeRegion | undefined, totalLines: number) {
	if (visibleRegion === undefined) {
		return {
			startLine: 1,
			endLine: totalLines
		};
	}
	return {
		startLine: Math.max(visibleRegion.startLine, 1),
		endLine: Math.min(visibleRegion.endLine, totalLines)
	};
}

function asCodeLineSelection(
	selection: CodeLineSelection | CodeOffsetSelection | undefined,
	lineStartOffsets: number[]
): CodeLineSelection | undefined {
	if (!selection || 'firstLine' in selection) {
		return selection;
	}
	return new CodeLineSelection(
		CodeOffsetSelection.getLineFromOffset(lineStartOffsets, selection.startOffset),
		CodeOffsetSelection.getLineFromOffset(lineStartOffsets, selection.endOffset)
	);
}

function getLineStartOffsets(lines: string[], codeElement: RenderableCode): number[] {
	const lineOffsets: number[] = [];
	let offset = 0;
	let firstLine = 1;
	if ('firstOffset' in codeElement) {
		firstLine = codeElement.firstLine;
		for (let lineNumber = 1; lineNumber < firstLine; ++lineNumber) {
			lineOffsets[lineNumber] = offset;
		}
	}
	for (let lineNumber = firstLine; lineNumber <= lines.length; ++lineNumber) {
		const lineIndex = lineNumber - 1;
		lineOffsets[lineIndex] = offset;
		offset += 1 + lines[lineIndex]!.length;
	}
	return lineOffsets;
}

function buildProbeCoverageContainer(probeCoverage: ProbeCoverageInfo | undefined): ProbeCoverageContainer {
	const probesByLine = new Map<number, DecisionProbe | SimpleStatementCoverageProbe>();
	if (!probeCoverage) {
		return { probesByLine, columnWidthInEm: 0 };
	}
	let maxTrueCount = 0;
	let maxFalseCount = 0;
	probeCoverage.probes.forEach(probe => {
		probesByLine.set(probe.line, probe);
		if ('executionCount' in probe && probe.executionCount > maxTrueCount) {
			maxTrueCount = probe.executionCount;
		}
		if ('trueExecutionCount' in probe && probe.trueExecutionCount > maxTrueCount) {
			maxTrueCount = probe.trueExecutionCount;
		}
		if ('falseExecutionCount' in probe && probe.falseExecutionCount > maxFalseCount) {
			maxFalseCount = probe.falseExecutionCount;
		}
	});
	return { probesByLine, columnWidthInEm: (String(maxTrueCount).length + String(maxFalseCount).length) * 0.6 };
}

function buildLineCoverageByLine(
	lineCoverage: LineCoverageInfo | DerivedTestCoverageInfo | undefined
): Map<number, ELineCoverageEntry> {
	const coverageStates = new Map<number, ELineCoverageEntry>();
	if (!lineCoverage || !('fullyCoveredLines' in lineCoverage)) {
		return coverageStates;
	}
	const coverageFields = ['fullyCoveredLines', 'partiallyCoveredLines', 'uncoveredLines'] as const;
	const coverageTypes: ELineCoverageEntry[] = [
		ELineCoverage.FULLY_COVERED.name,
		ELineCoverage.PARTIALLY_COVERED.name,
		ELineCoverage.NOT_COVERED.name
	];
	for (let j = 0; j < coverageFields.length; j++) {
		const affectedLinesForType = lineCoverage[coverageFields[j]!];
		for (const line of affectedLinesForType) {
			coverageStates.set(line, coverageTypes[j]!);
		}
	}
	return coverageStates;
}
